import type { RealtimeChannel } from '@supabase/supabase-js';
import { authState } from './auth';
import { supabase, isSupabaseConfigured } from './supabase';

function currentFactoryName(): string | null {
  if (authState.profile?.role === 'factory' && authState.profile.company) {
    return authState.profile.company.trim() || null;
  }
  const meta = authState.user?.user_metadata?.company;
  return typeof meta === 'string' && meta.trim() ? meta.trim() : null;
}

export type Conversation = {
  id: string;
  buyerId: string;
  factoryName: string;
  productId?: string | null;
  productName?: string | null;
  createdAt: string;
  updatedAt: string;
  lastBody?: string;
  lastAt?: string;
  unreadCount?: number;
};

export type ChatMessage = {
  id: string;
  conversationId: string;
  senderId: string;
  body: string;
  createdAt: string;
  readAt?: string | null;
};

const LOCAL_CONV_KEY = 'forgelink_conversations';
const LOCAL_MSG_KEY = 'forgelink_messages';

function scope(): string {
  return authState.user?.id ? String(authState.user.id) : 'guest';
}

function readLocal<T>(base: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`${base}:${scope()}`);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeLocal(base: string, value: unknown): void {
  localStorage.setItem(`${base}:${scope()}`, JSON.stringify(value));
}

function mapConversation(row: Record<string, unknown>): Conversation {
  return {
    id: String(row.id),
    buyerId: String(row.buyer_id),
    factoryName: String(row.factory_name),
    productId: row.product_id != null ? String(row.product_id) : null,
    productName: row.product_name != null ? String(row.product_name) : null,
    createdAt: String(row.created_at),
    updatedAt: String(row.updated_at ?? row.created_at),
  };
}

function mapMessage(row: Record<string, unknown>): ChatMessage {
  return {
    id: String(row.id),
    conversationId: String(row.conversation_id),
    senderId: String(row.sender_id),
    body: String(row.body),
    createdAt: String(row.created_at),
    readAt: row.read_at != null ? String(row.read_at) : null,
  };
}

export function messagingAvailable(): boolean {
  return isSupabaseConfigured && Boolean(supabase) && Boolean(authState.user);
}

/** Get or create a buyer↔factory thread. Product is optional context. */
export async function getOrCreateConversation(opts: {
  factoryName: string;
  productId?: string | null;
  productName?: string | null;
}): Promise<Conversation> {
  const factoryName = opts.factoryName.trim();
  if (!factoryName) throw new Error('Factory name is required');
  const buyerId = authState.user?.id;
  if (!buyerId) throw new Error('Sign in to message the seller');

  if (!messagingAvailable() || !supabase) {
    return getOrCreateLocalConversation({
      buyerId,
      factoryName,
      productId: opts.productId,
      productName: opts.productName,
    });
  }

  const { data: existing, error: selErr } = await supabase
    .from('conversations')
    .select('*')
    .eq('buyer_id', buyerId)
    .eq('factory_name', factoryName)
    .maybeSingle();

  if (selErr) {
    // Table / RLS missing → local fallback
    console.warn('conversations select failed:', selErr.message);
    return getOrCreateLocalConversation({
      buyerId,
      factoryName,
      productId: opts.productId,
      productName: opts.productName,
    });
  }

  if (existing) {
    const conv = mapConversation(existing as Record<string, unknown>);
    // Refresh product context when opening from a different product
    if (opts.productId && opts.productId !== conv.productId) {
      const { data: updated } = await supabase
        .from('conversations')
        .update({
          product_id: opts.productId,
          product_name: opts.productName || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', conv.id)
        .select('*')
        .maybeSingle();
      if (updated) return mapConversation(updated as Record<string, unknown>);
    }
    return conv;
  }

  const { data: inserted, error: insErr } = await supabase
    .from('conversations')
    .insert({
      buyer_id: buyerId,
      factory_name: factoryName,
      product_id: opts.productId || null,
      product_name: opts.productName || null,
    })
    .select('*')
    .single();

  if (insErr || !inserted) {
    console.warn('conversations insert failed:', insErr?.message);
    return getOrCreateLocalConversation({
      buyerId,
      factoryName,
      productId: opts.productId,
      productName: opts.productName,
    });
  }
  return mapConversation(inserted as Record<string, unknown>);
}

function getOrCreateLocalConversation(opts: {
  buyerId: string;
  factoryName: string;
  productId?: string | null;
  productName?: string | null;
}): Conversation {
  const list = readLocal<Conversation[]>(LOCAL_CONV_KEY, []);
  let found = list.find(
    (c) => c.buyerId === opts.buyerId && c.factoryName === opts.factoryName,
  );
  if (found) {
    if (opts.productId) {
      found = {
        ...found,
        productId: opts.productId,
        productName: opts.productName || found.productName,
        updatedAt: new Date().toISOString(),
      };
      const next = list.map((c) => (c.id === found!.id ? found! : c));
      writeLocal(LOCAL_CONV_KEY, next);
    }
    return found;
  }
  const now = new Date().toISOString();
  const created: Conversation = {
    id: crypto.randomUUID(),
    buyerId: opts.buyerId,
    factoryName: opts.factoryName,
    productId: opts.productId || null,
    productName: opts.productName || null,
    createdAt: now,
    updatedAt: now,
  };
  list.unshift(created);
  writeLocal(LOCAL_CONV_KEY, list);
  return created;
}

export async function listConversationsForCurrentUser(): Promise<Conversation[]> {
  const uid = authState.user?.id;
  if (!uid) return [];

  const factory = currentFactoryName();
  const asFactory = authState.profile?.role === 'factory' && Boolean(factory);

  if (!messagingAvailable() || !supabase) {
    return enrichLocalConversations(
      readLocal<Conversation[]>(LOCAL_CONV_KEY, []).filter((c) =>
        asFactory ? c.factoryName === factory : c.buyerId === uid,
      ),
    );
  }

  let query = supabase.from('conversations').select('*').order('updated_at', { ascending: false });
  if (asFactory && factory) {
    query = query.eq('factory_name', factory);
  } else {
    query = query.eq('buyer_id', uid);
  }

  const { data, error } = await query;
  if (error) {
    console.warn('list conversations failed:', error.message);
    return enrichLocalConversations(
      readLocal<Conversation[]>(LOCAL_CONV_KEY, []).filter((c) =>
        asFactory ? c.factoryName === factory : c.buyerId === uid,
      ),
    );
  }

  const convs = (data || []).map((r) => mapConversation(r as Record<string, unknown>));
  return enrichRemoteConversations(convs);
}

async function enrichRemoteConversations(convs: Conversation[]): Promise<Conversation[]> {
  if (!supabase || !convs.length) return convs;
  const uid = authState.user?.id;
  const enriched: Conversation[] = [];
  for (const c of convs) {
    const { data: msgs } = await supabase
      .from('messages')
      .select('body, created_at, sender_id, read_at')
      .eq('conversation_id', c.id)
      .order('created_at', { ascending: false })
      .limit(40);
    const rows = (msgs || []) as Record<string, unknown>[];
    const last = rows[0];
    const unread = rows.filter(
      (m) => m.sender_id !== uid && m.read_at == null,
    ).length;
    enriched.push({
      ...c,
      lastBody: last ? String(last.body) : undefined,
      lastAt: last ? String(last.created_at) : c.updatedAt,
      unreadCount: unread,
    });
  }
  return enriched.sort((a, b) =>
    String(b.lastAt || b.updatedAt).localeCompare(String(a.lastAt || a.updatedAt)),
  );
}

function enrichLocalConversations(convs: Conversation[]): Conversation[] {
  const allMsgs = readLocal<ChatMessage[]>(LOCAL_MSG_KEY, []);
  const uid = authState.user?.id;
  return convs
    .map((c) => {
      const msgs = allMsgs
        .filter((m) => m.conversationId === c.id)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      const last = msgs[msgs.length - 1];
      const unread = msgs.filter((m) => m.senderId !== uid && !m.readAt).length;
      return {
        ...c,
        lastBody: last?.body,
        lastAt: last?.createdAt || c.updatedAt,
        unreadCount: unread,
      };
    })
    .sort((a, b) =>
      String(b.lastAt || b.updatedAt).localeCompare(String(a.lastAt || a.updatedAt)),
    );
}

export async function listMessages(conversationId: string): Promise<ChatMessage[]> {
  if (!conversationId) return [];

  if (!messagingAvailable() || !supabase) {
    return readLocal<ChatMessage[]>(LOCAL_MSG_KEY, [])
      .filter((m) => m.conversationId === conversationId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  if (error) {
    console.warn('list messages failed:', error.message);
    return readLocal<ChatMessage[]>(LOCAL_MSG_KEY, [])
      .filter((m) => m.conversationId === conversationId)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }
  return (data || []).map((r) => mapMessage(r as Record<string, unknown>));
}

export async function sendMessage(
  conversationId: string,
  body: string,
): Promise<ChatMessage> {
  const trimmed = body.trim();
  if (!trimmed) throw new Error('Message cannot be empty');
  if (trimmed.length > 4000) throw new Error('Message is too long');
  const senderId = authState.user?.id;
  if (!senderId) throw new Error('Sign in to send a message');

  if (!messagingAvailable() || !supabase) {
    return sendLocalMessage(conversationId, senderId, trimmed);
  }

  const { data, error } = await supabase
    .from('messages')
    .insert({
      conversation_id: conversationId,
      sender_id: senderId,
      body: trimmed,
    })
    .select('*')
    .single();

  if (error || !data) {
    console.warn('send message failed:', error?.message);
    // If remote fails (migration missing), keep a local copy so the UI still works.
    return sendLocalMessage(conversationId, senderId, trimmed);
  }
  return mapMessage(data as Record<string, unknown>);
}

function sendLocalMessage(
  conversationId: string,
  senderId: string,
  body: string,
): ChatMessage {
  const msg: ChatMessage = {
    id: crypto.randomUUID(),
    conversationId,
    senderId,
    body,
    createdAt: new Date().toISOString(),
    readAt: null,
  };
  const list = readLocal<ChatMessage[]>(LOCAL_MSG_KEY, []);
  list.push(msg);
  writeLocal(LOCAL_MSG_KEY, list);

  const convs = readLocal<Conversation[]>(LOCAL_CONV_KEY, []);
  const next = convs.map((c) =>
    c.id === conversationId ? { ...c, updatedAt: msg.createdAt } : c,
  );
  writeLocal(LOCAL_CONV_KEY, next);
  return msg;
}

export async function markConversationRead(conversationId: string): Promise<void> {
  const uid = authState.user?.id;
  if (!uid || !conversationId) return;

  if (!messagingAvailable() || !supabase) {
    const list = readLocal<ChatMessage[]>(LOCAL_MSG_KEY, []);
    const now = new Date().toISOString();
    writeLocal(
      LOCAL_MSG_KEY,
      list.map((m) =>
        m.conversationId === conversationId && m.senderId !== uid && !m.readAt
          ? { ...m, readAt: now }
          : m,
      ),
    );
    return;
  }

  await supabase
    .from('messages')
    .update({ read_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .is('read_at', null)
    .neq('sender_id', uid);
}

export type MessageListener = (messages: ChatMessage[]) => void;

/**
 * Subscribe to new messages. Prefer Realtime; fall back to short polling.
 * Returns an unsubscribe function.
 */
export function subscribeToConversation(
  conversationId: string,
  onChange: MessageListener,
): () => void {
  let stopped = false;
  let channel: RealtimeChannel | null = null;
  let pollTimer: ReturnType<typeof setInterval> | undefined;

  const refresh = async () => {
    if (stopped) return;
    try {
      const msgs = await listMessages(conversationId);
      if (!stopped) onChange(msgs);
    } catch (err) {
      console.warn('message refresh failed', err);
    }
  };

  void refresh();

  if (messagingAvailable() && supabase) {
    try {
      channel = supabase
        .channel(`messages:${conversationId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${conversationId}`,
          },
          () => {
            void refresh();
          },
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') return;
          // If realtime never connects, polling still covers us.
        });
    } catch (err) {
      console.warn('realtime subscribe failed', err);
    }
  }

  pollTimer = setInterval(() => {
    void refresh();
  }, 4000);

  return () => {
    stopped = true;
    if (pollTimer) clearInterval(pollTimer);
    if (channel && supabase) {
      void supabase.removeChannel(channel);
    }
  };
}

export async function getConversationById(id: string): Promise<Conversation | null> {
  if (!id) return null;
  if (!messagingAvailable() || !supabase) {
    return readLocal<Conversation[]>(LOCAL_CONV_KEY, []).find((c) => c.id === id) || null;
  }
  const { data, error } = await supabase
    .from('conversations')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error || !data) {
    return readLocal<Conversation[]>(LOCAL_CONV_KEY, []).find((c) => c.id === id) || null;
  }
  return mapConversation(data as Record<string, unknown>);
}
